import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { APPOINTMENT_STATUS_LABELS, type AppointmentStatus } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import type { WeekAppointment } from "@/lib/dashboard-week";
import { useEmOutraOrganizacao, useIrParaOFoco } from "@/components/organization/painel/OrganizacaoEmFoco";
import { usePodeMarcarChegada } from "@/lib/paciente-chegou";
import { ACAO_CHEGOU, acaoDaConsulta } from "./acao-da-consulta";

/**
 * O que o diálogo da consulta pode fazer, dentro da clínica ativa ou fora dela (consulta aberta pela
 * conta Pessoal ou pelo painel de /organizacoes). Fora: só o profissional da consulta inicia e
 * encerra (a API confere também), iniciar não sai da tela, e ir para a ficha troca de organização
 * com "Desfazer" — a ficha é onde o atendimento se registra, e ela é da clínica.
 */
export function useAcoesDaConsulta(appointment: WeekAppointment | null, onClose: () => void) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const fora = useEmOutraOrganizacao();
  const irParaOFoco = useIrParaOFoco();
  const podeAgendar = usePermissao("agenda.manage");
  const podeMarcarChegada = usePodeMarcarChegada();
  // O nome vem da própria consulta: dentro do foco o useAuth ainda está carregando no primeiro instante.
  const clinica = appointment?.organizationName ?? user?.activeOrganization?.name ?? "a clínica";

  // Fora, o diálogo não fecha ao iniciar: o status mostrado passa a ser o que a API devolveu.
  const [statusLocal, setStatusLocal] = useState<AppointmentStatus | null>(null);
  useEffect(() => setStatusLocal(null), [appointment?.id]);
  const status = statusLocal ?? appointment?.status ?? null;
  const podeAtender = !fora || (podeAgendar && !!appointment?.dentistId && appointment.dentistId === user?.id);
  const carregado = !fora || !!user;

  const ir = (href: string) => {
    onClose();
    if (fora && irParaOFoco) irParaOFoco(clinica, href);
    else navigate(href);
  };
  const fichaDoAtendimento = (a: { id: string; patientId: string | null }) => `/pacientes/${a.patientId}?atendimento=${a.id}`;

  const invalidar = () => {
    utils.appointment.invalidate();
    utils.team.list.invalidate();
    // Planta e lista "Agora" das salas leem o mesmo atendimento.
    utils.salas.agora.invalidate();
  };
  const iniciar = trpc.appointment.iniciarAtendimento.useMutation({
    onSuccess: (a: any) => {
      invalidar();
      if (fora) {
        setStatusLocal("IN_PROGRESS");
        toast.success(`Atendimento iniciado em ${clinica}.`, a?.patientId ? { action: { label: "Ir para o atendimento", onClick: () => ir(fichaDoAtendimento(a)) } } : undefined);
        return;
      }
      toast.success(`Atendimento de ${a?.patientName ?? "paciente"} iniciado.`);
      onClose();
      // Mesmo destino da Agenda: a ficha em modo atendimento é a tela de trabalho.
      if (a?.patientId) navigate(fichaDoAtendimento(a));
    },
    onError: (e) => toast.error(e.message || "Erro ao iniciar o atendimento"),
  });
  const encerrar = trpc.appointment.encerrarAtendimento.useMutation({
    onSuccess: () => {
      toast.success("Atendimento encerrado.");
      invalidar();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Erro ao encerrar o atendimento"),
  });

  // Trocar o status (paciente chegou, faltou...), como no cartão da Agenda. Só na clínica ativa.
  const atualizar = trpc.appointment.update.useMutation({
    onSuccess: (_r, v) => {
      setStatusLocal(v.data.status as AppointmentStatus);
      invalidar();
      toast.success(`Status: ${APPOINTMENT_STATUS_LABELS[v.data.status as AppointmentStatus]}`);
    },
    onError: (e) => toast.error(e.message || "Erro ao mudar o status"),
  });

  const chegada = !fora && podeAgendar && !!status && !!appointment && podeMarcarChegada(status, appointment.date);
  const base = chegada ? ACAO_CHEGOU : status ? acaoDaConsulta(status) : null;
  const acao = base && fora && base.tipo === "continuar" ? { ...base, rotulo: "Ir para o atendimento" } : base;
  // Iniciar e reagendar são de quem atende; continuar e ver levam pra ficha, que precisa de paciente.
  const acaoDisponivel =
    !!acao &&
    (acao.tipo === "chegou" || (acao.tipo === "iniciar" || acao.tipo === "reagendar" ? podeAtender : !!appointment?.patientId));

  const executarAcao = () => {
    if (!appointment || !acao) return;
    if (acao.tipo === "chegou") return atualizar.mutate({ id: appointment.id, data: { status: "WAITING" } });
    if (acao.tipo === "iniciar") return iniciar.mutate({ id: appointment.id });
    if (acao.tipo === "continuar") return ir(fichaDoAtendimento(appointment));
    if (acao.tipo === "verAtendimento") return ir(`/pacientes/${appointment.patientId}?aba=historico`);
    return ir(`/agenda?reagendar=${appointment.id}`);
  };

  return {
    status,
    acao,
    acaoDisponivel,
    executarAcao,
    podeEncerrar: status === "IN_PROGRESS" && podeAtender,
    encerrar: () => appointment && encerrar.mutate({ id: appointment.id }),
    pendente: iniciar.isPending || encerrar.isPending || atualizar.isPending,
    podeMudarStatus: !fora && podeAgendar,
    mudarStatus: (s: AppointmentStatus) =>
      appointment && s !== status && atualizar.mutate({ id: appointment.id, data: { status: s, ...(s === "CANCELLED" ? { cancelledBy: "professional" as const } : {}) } }),
    verPaciente: () => appointment?.patientId && ir(`/pacientes/${appointment.patientId}`),
    /** Nome da clínica quando a consulta é de fora da organização ativa (pra faixa do diálogo). */
    clinicaDeFora: fora ? clinica : null,
    somenteLeitura: fora && carregado && !podeAtender,
  };
}
